import { format } from "date-fns";
import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { emptyCustomerEmployeeRate, type U3120CustomerEmployeeRate } from "../../../../U3120Service";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";

// Gestion des handlers pour les taux horaires d'un client
export interface U3120CustomerEmployeeRateHandler {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3120CustomerEmployeeRate>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3120CustomerEmployeeRate>>;
  dataChangedEvent: number;
}

// Paramètres pour les handlers des taux horaires d'un client
export interface U3120CustomerEmployeeRateHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3120CustomerEmployeeRate>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3120CustomerEmployeeRate>>;
}

// Gestion des handlers pour les taux horaires d'un client
export interface U3120CustomerEmployeeRateHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;

  onDlgClose: (result: string) => void;

  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;

  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3120CustomerEmployeeRate>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3120CustomerEmployeeRate>>;
  dataChangedEvent: number;
}

// Implémentation des handlers pour les taux horaires d'un client
export function u3120CustomerEmployeeRateHandler(params: U3120CustomerEmployeeRateHandlerParam): U3120CustomerEmployeeRateHandlerReturn {
  const { gridStateHandlers, formStateHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  // Gestion de l'ajout d'un taux horaire
  const onAdd = (): void => {
    // setEditMode(ADD) doit être appelé avant setData : il réinitialise avec newRecord(), qu'on écrase ensuite avec la date par défaut
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setData({
      ...emptyCustomerEmployeeRate,
      validFrom: format(new Date(), "yyyy-MM-dd"),
    });
    setDialogOpen(true);
  };

  // Gestion de la modification d'un taux horaire
  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    formStateHandlers.setData(selectedRow as U3120CustomerEmployeeRate);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
    return;
  };

  // Gestion de la suppression d'un taux horaire
  const onDelete = (): void => {
    setDeleteDialogOpen(true);
    return;
  };

  // Gestion de la fermeture du dialog de modification/ajout
  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      // Valider les données avant de sauvegarder
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers && businessHandlers.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);
        formStateHandlers.saveData();
        // Si des erreurs de validation existent, ne pas fermer le dialogue
        if (Object.keys(errors).length > 0) {
          return; // Ne pas fermer le dialogue si des erreurs de validation existent
        }
      }
      if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
        // important: mettre à jour la ligne dans le gridStateHandlers
        const updatedData = gridStateHandlers.getData().map((item) => (item.id === formStateHandlers.getData().id ? { ...formStateHandlers.getData() } : item));
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent(dataChangedEvent + 1);
      } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
        const newData = formStateHandlers.getData();

        // Générer un UUID temporaire pour les nouveaux enregistrements
        newData.id = generateTemporaryId();
        gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        setDataChangedEvent(dataChangedEvent + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
    }
    setDialogOpen(false);
    return;
  };

  // Gestion de la fermeture du dialog de suppression
  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);

      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
    return;
  };

  return { onAdd, onModify, onDelete, onDlgClose, isDialogOpen, setDialogOpen, formStateHandlers, gridStateHandlers, isDeleteDialogOpen, onDlgDeleteClose, dataChangedEvent };
}
